// src/components/MFAEnrollment.tsx
import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import toast from 'react-hot-toast';
import { axiosInstance } from '../utils/axiosConfig';

interface MFAEnrollmentProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const MFAEnrollment: React.FC<MFAEnrollmentProps> = ({ isOpen, onClose, onSuccess }) => {
  const [step, setStep] = useState<'initial' | 'totp' | 'sms'>('initial');
  const [loading, setLoading] = useState(false);
  const [mfaSecret, setMfaSecret] = useState('');
  const [otpauthUrl, setOtpauthUrl] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [phone, setPhone] = useState('');
  const [smsSent, setSmsSent] = useState(false);

  // TOTP (authenticator app) enrollment
  const handleEnrollTOTP = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.post('/auth/mfa/enroll');
      setMfaSecret(response.data.secret);
      setOtpauthUrl(response.data.otpauthUrl);
      setStep('totp');
      toast.success('Scan the QR code with your authenticator app');
    } catch (err: any) {
      const error = err.response?.data?.error || 'Failed to initiate MFA enrollment';
      toast.error(error);
    } finally {
      setLoading(false);
    }
  };

  // SMS enrollment
  const handleSendSMS = async () => {
    if (!phone.match(/^\+?[1-9]\d{1,14}$/)) {
      toast.error('Enter a valid phone number with country code');
      return;
    }
    setLoading(true);
    try {
      await axiosInstance.post('/auth/mfa/enroll-sms', { phone });
      setSmsSent(true);
      toast.success('SMS code sent!');
    } catch (err: any) {
      const error = err.response?.data?.error || 'Failed to send SMS';
      toast.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Verify TOTP or SMS
  const handleVerify = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }
    setLoading(true);
    try {
      if (step === 'totp') {
        await axiosInstance.post('/auth/mfa/verify-enrollment', { secret: mfaSecret, code: verificationCode });
      } else if (step === 'sms') {
        await axiosInstance.post('/auth/mfa/verify-enrollment-sms', { code: verificationCode, phone });
      }
      toast.success('MFA enabled successfully!');
      setStep('initial');
      setVerificationCode('');
      setMfaSecret('');
      setOtpauthUrl('');
      setPhone('');
      setSmsSent(false);
      onSuccess?.();
      onClose();
    } catch (err: any) {
      const error = err.response?.data?.error || 'Invalid verification code';
      toast.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setStep('initial');
    setVerificationCode('');
    setMfaSecret('');
    setOtpauthUrl('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
      }}
    >
      <div
        style={{
          background: 'white',
          padding: '40px',
          borderRadius: '12px',
          maxWidth: '500px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
          width: '90%',
        }}
      >
        {step === 'initial' && (
          <>
            <h3>Enable Two-Factor Authentication</h3>
            <p style={{ color: '#666', marginBottom: '20px' }}>
              Choose your preferred MFA method:
            </p>
            <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
              <button
                onClick={handleEnrollTOTP}
                disabled={loading}
                style={{
                  flex: 1,
                  padding: '14px',
                  background: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: 600,
                  cursor: loading ? 'not-allowed' : 'pointer',
                }}
              >
                Authenticator App
              </button>
              <button
                onClick={() => setStep('sms')}
                disabled={loading}
                style={{
                  flex: 1,
                  padding: '14px',
                  background: '#2196F3',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: 600,
                  cursor: loading ? 'not-allowed' : 'pointer',
                }}
              >
                SMS
              </button>
            </div>
            <button
              onClick={handleCancel}
              style={{
                padding: '10px 20px',
                background: '#f0f0f0',
                color: '#666',
                border: '1px solid #ccc',
                borderRadius: '6px',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </>
        )}
        {step === 'totp' && (
          <>
            <h3>Verify Your Authenticator</h3>
            <p style={{ color: '#666', marginBottom: '20px' }}>
              Scan this QR code with your authenticator app:
            </p>
            {otpauthUrl && (
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <QRCodeSVG
                  value={otpauthUrl}
                  size={200}
                  level="H"
                  includeMargin={true}
                  style={{ border: '2px solid #f0f0f0', padding: '10px' }}
                />
              </div>
            )}
            <p style={{ color: '#999', fontSize: '0.9em', marginBottom: '20px' }}>
              Can't scan? Enter this code manually:
              <br />
              <code
                style={{
                  background: '#f5f5f5',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  display: 'inline-block',
                  marginTop: '8px',
                  fontFamily: 'monospace',
                  letterSpacing: '1px',
                }}
              >
                {mfaSecret}
              </code>
            </p>
            <div style={{ marginBottom: '20px' }}>
              <label>
                Enter the 6-digit code from your authenticator:
              </label>
              <input
                type="text"
                maxLength={6}
                placeholder="000000"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                style={{
                  width: '100%',
                  padding: '10px',
                  marginTop: '8px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '1.2em',
                  letterSpacing: '2px',
                  textAlign: 'center',
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setStep('initial')}
                disabled={loading}
                style={{
                  padding: '10px 20px',
                  background: '#f0f0f0',
                  color: '#666',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                }}
              >
                Back
              </button>
              <button
                onClick={handleVerify}
                disabled={loading || verificationCode.length !== 6}
                style={{
                  padding: '10px 20px',
                  background: verificationCode.length === 6 && !loading ? '#4CAF50' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: verificationCode.length === 6 && !loading ? 'pointer' : 'not-allowed',
                }}
              >
                {loading ? 'Verifying...' : 'Enable MFA'}
              </button>
            </div>
          </>
        )}
        {step === 'sms' && (
          <>
            <h3>Enable SMS Authentication</h3>
            <p style={{ color: '#666', marginBottom: '20px' }}>
              Enter your mobile number to receive a verification code via SMS.
            </p>
            <input
              type="tel"
              placeholder="+1 555 123 4567"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                marginBottom: '16px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '1em',
              }}
              disabled={smsSent}
            />
            {!smsSent ? (
              <button
                onClick={handleSendSMS}
                disabled={loading || !phone}
                style={{
                  padding: '10px 20px',
                  background: '#2196F3',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  marginBottom: '16px',
                }}
              >
                {loading ? 'Sending...' : 'Send Code'}
              </button>
            ) : (
              <>
                <div style={{ marginBottom: '20px' }}>
                  <label>Enter the 6-digit code sent to your phone:</label>
                  <input
                    type="text"
                    maxLength={6}
                    placeholder="000000"
                    value={verificationCode}
                    onChange={e => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                    style={{
                      width: '100%',
                      padding: '10px',
                      marginTop: '8px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '1.2em',
                      letterSpacing: '2px',
                      textAlign: 'center',
                    }}
                  />
                </div>
                <button
                  onClick={handleVerify}
                  disabled={loading || verificationCode.length !== 6}
                  style={{
                    padding: '10px 20px',
                    background: verificationCode.length === 6 && !loading ? '#2196F3' : '#ccc',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: verificationCode.length === 6 && !loading ? 'pointer' : 'not-allowed',
                  }}
                >
                  {loading ? 'Verifying...' : 'Enable SMS MFA'}
                </button>
              </>
            )}
            <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
              <button
                onClick={() => {
                  setStep('initial');
                  setPhone('');
                  setVerificationCode('');
                  setSmsSent(false);
                }}
                disabled={loading}
                style={{
                  padding: '10px 20px',
                  background: '#f0f0f0',
                  color: '#666',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                }}
              >
                Back
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MFAEnrollment;
